import { Capacitor } from '@capacitor/core';
import { throttle } from 'throttle-debounce';
import captureSpecificError from '../capture-error';
import { telemetry } from '../backend';

const degtorad = Math.PI / 180; // Degree-to-Radian conversion

export const calculateCompassHeading = (
  alpha: DeviceOrientationEvent['alpha'],
  beta: DeviceOrientationEvent['beta'],
  gamma: DeviceOrientationEvent['gamma'],
) => {
  // Calculation from https://www.w3.org/TR/orientation-event/#worked-example
  const betaRadians = beta ? beta * degtorad : 0; // beta value
  const gammaRadians = gamma ? gamma * degtorad : 0; // gamma value
  const alphaRadians = alpha ? alpha * degtorad : 0; // alpha value

  const cY = Math.cos(gammaRadians);
  const cZ = Math.cos(alphaRadians);
  const sX = Math.sin(betaRadians);
  const sY = Math.sin(gammaRadians);
  const sZ = Math.sin(alphaRadians);

  // Calculate Vx and Vy components
  const Vx = -cZ * sY - sZ * sX * cY;
  const Vy = -sZ * sY + cZ * sX * cY;

  // Calculate compass heading
  let compassHeading = Math.atan(Vx / Vy);

  // Convert compass heading to use whole unit circle
  if (Vy < 0) {
    compassHeading += Math.PI;
  } else if (Vx < 0) {
    compassHeading += 2 * Math.PI;
  }

  return compassHeading * (180 / Math.PI); // Compass Heading (in degrees)
};

export const startWatchingOrientation = async (
  deviceOrientationCallback: (data: DeviceOrientationEvent) => any,
) => {
  try {
    if (!Capacitor.isNativePlatform()) return;
    const platform = Capacitor.getPlatform();
    if (platform === 'ios') {
      // @ts-expect-error -- `requestPermission` only exists on iOS Safari
      await window.DeviceOrientationEvent.requestPermission();
    }
  } catch (error) {
    const telemetryMessage = `[orientation.startWatchingOrientation] Failed to request permission ${
      (error as any)?.message
    }`;
    // eslint-disable-next-line no-bitwise
    telemetry(telemetry.ERROR, telemetryMessage);
    captureSpecificError(
      telemetryMessage,
      'containers/app-main-client/src/services/orientation/index.js',
    );
  }

  if (Capacitor.isNativePlatform()) {
    const throttleDeviceOrientationLength =
      (window.DECKEE_CONFIG &&
        window.DECKEE_CONFIG.logTrips &&
        window.DECKEE_CONFIG.logTrips.throttleDeviceOrientationLength) ||
      500;
    const throttledUpdateBearing = throttle(
      throttleDeviceOrientationLength,
      deviceOrientationCallback,
    );

    window.addEventListener('deviceorientation', throttledUpdateBearing, true);
  }
};

export const stopWatchingOrientation = () => {
  // @ts-expect-error -- This may be broken as the original event listener function isn't passed in
  window.removeEventListener('deviceorientation');
};
